import { useEffect, useCallback } from 'react';

import { Container, Flex, FloraButton } from '@grupoboticario/flora-react';
import { MagazineIcon } from '@grupoboticario/flora-react-icons';
import {
  Carousel,
  CarouselContent,
  NextButton,
  PreviousButton,
} from '@grupoboticario/flora-team-react-carousel';
import { Link } from 'react-router-dom';

import { PageInfo, EmptyState, ResponsiveFloraTypography } from 'components';
import config from 'config';
import { isMobile as isMobileHelper, shouldRenderCatalogsCarousel } from 'helpers';
import { useCurrentCatalogs, useNextCatalogs, useTracking } from 'hooks';

import Styles from './Catalogs.styles';
import { CardCatalog, CardCatalogSkeleton } from './components';

import type { CardCatalogProps } from './components';
import type { MLDCatalog } from 'domains/catalog';

const minLengthDesktop = 3;
const minLengthMobile = 1;
const slideToShow = {
  mobile: 1,
  desktop: 3,
};

export const Catalogs = () => {
  const {
    data: currentCatalogs,
    isLoading: isLoadingCurrentCatalogs,
    isSuccess: isSuccessCurrentCatalogs,
  } = useCurrentCatalogs();
  const { trackWithUserData, sendDataToGTM } = useTracking();

  const {
    data: nextCatalogs,
    starlinkData: starlinkNextCatalogs,
    isLoading: isLoadingNextCatalogs,
    isSuccess: isSuccessNextCatalogs,
  } = useNextCatalogs();

  useEffect(() => {
    if (!isLoadingCurrentCatalogs) {
      trackWithUserData({
        ...(!isSuccessCurrentCatalogs || !isSuccessNextCatalogs
          ? { cd_error_message: 'tivemos-um-problema-para-carregar-os-catalogos-do-ciclo' }
          : {}),
      });
    }
  }, [
    isLoadingCurrentCatalogs,
    isSuccessCurrentCatalogs,
    isSuccessNextCatalogs,
    trackWithUserData,
  ]);

  const isMobile = isMobileHelper();

  const handleTrackChange = useCallback(
    (index: number | null, section: string, detail: string) => {
      sendDataToGTM({
        event: 'analytics-event',
        event_parameters: {
          event_name: 'interaction_catalogos',
          cd_interaction_detail: `click:${!!index ? detail + ':' + index : detail}`,
          cd_section: section || '',
        },
      });
    },
    [sendDataToGTM]
  );

  return (
    <Flex direction='column' gap='$4'>
      <Container size='large'>
        <PageInfo
          breadcrumbs={[
            {
              text: config.app.catalogName,
              url: '',
            },
          ]}
        />
        <Flex direction='column' gap='$2'>
          <ResponsiveFloraTypography fontSize='title' as='h1' fontWeight='bold'>
            {config.app.catalogName}
          </ResponsiveFloraTypography>

          <ResponsiveFloraTypography>
            Compartilhe a Minha Loja Digital e receba novas vendas direto no WhatsApp.
          </ResponsiveFloraTypography>
        </Flex>
      </Container>

      <Styles.Wrapper>
        <Container size='large'>
          <Styles.SectionWrapper direction='column' gap='$6'>
            <Flex gap='$6' align='center'>
              <ResponsiveFloraTypography fontSize='title' fontWeight='medium'>
                Ciclo atual
              </ResponsiveFloraTypography>
              {!isLoadingCurrentCatalogs &&
                shouldRenderCatalogsCarousel(
                  currentCatalogs,
                  minLengthDesktop,
                  minLengthMobile
                ) && (
                  <FloraButton
                    as={Link}
                    hierarchy='secondary'
                    size='small'
                    to='/catalogos-dos-ciclos/ciclo-atual'
                    onClick={() => handleTrackChange(null, 'ciclo-atual', 'ver-tudo')}
                  >
                    Ver tudo
                  </FloraButton>
                )}
            </Flex>

            {isLoadingCurrentCatalogs && (
              <Styles.Catalogs>
                {[...Array(3)].map((_, index) => (
                  <CardCatalogSkeleton key={index} />
                ))}
              </Styles.Catalogs>
            )}

            {!isLoadingCurrentCatalogs &&
            shouldRenderCatalogsCarousel(currentCatalogs, minLengthDesktop, minLengthMobile) ? (
              <Carousel
                onDotPress={(index) => {
                  handleTrackChange(index, 'ciclo-atual', 'carrossel');
                }}
                onNextPress={() => {
                  handleTrackChange(null, 'ciclo-atual', 'proximo');
                }}
                onPreviousPress={() => {
                  handleTrackChange(null, 'ciclo-atual', 'anterior');
                }}
              >
                <PreviousButton />
                <CarouselContent
                  slidesToShow={isMobile ? slideToShow.mobile : slideToShow.desktop}
                  infinite={false}
                >
                  {currentCatalogs.map((catalog: CardCatalogProps, index) => (
                    <Styles.CarouseItemlWrapper key={`current-catalogs-${catalog.brand}-${index}`}>
                      <CardCatalog
                        {...catalog}
                        link={catalog.link}
                        banner={catalog.banner}
                        index={index}
                      />
                    </Styles.CarouseItemlWrapper>
                  ))}
                </CarouselContent>
                <NextButton />
              </Carousel>
            ) : (
              <Styles.Catalogs>
                {Boolean(currentCatalogs && !isLoadingCurrentCatalogs) &&
                  currentCatalogs.map((catalog: MLDCatalog, index) => (
                    <CardCatalog
                      {...catalog}
                      key={`current-catalogs-${catalog.brand}-${index}`}
                      index={index}
                    />
                  ))}
              </Styles.Catalogs>
            )}
          </Styles.SectionWrapper>
          <Styles.SectionWrapper direction='column' gap='$6'>
            <Flex direction='column' gap='$4'>
              <Flex gap='$6' align='center'>
                <ResponsiveFloraTypography fontSize='title' fontWeight='medium'>
                  Próximo ciclo
                </ResponsiveFloraTypography>

                {!isLoadingNextCatalogs &&
                  shouldRenderCatalogsCarousel(nextCatalogs, minLengthDesktop, minLengthMobile) && (
                    <FloraButton
                      hierarchy='secondary'
                      size='small'
                      as={Link}
                      to='/catalogos-dos-ciclos/proximo-ciclo'
                      onClick={() => handleTrackChange(null, 'proximo-ciclo', 'ver-tudo')}
                    >
                      Ver tudo
                    </FloraButton>
                  )}
              </Flex>
              <ResponsiveFloraTypography fontSize='bodyLargeShort'>
                Descubra as novidades e planeje a sua revenda, aproveitando as oportunidades de cada
                ciclo.
              </ResponsiveFloraTypography>
            </Flex>
            {isLoadingNextCatalogs && (
              <Styles.Catalogs>
                {[...Array(3)].map((_, index) => (
                  <CardCatalogSkeleton key={index} />
                ))}
              </Styles.Catalogs>
            )}

            {!isSuccessNextCatalogs && starlinkNextCatalogs?.items.length === 0 ? (
              <EmptyState
                title='Materiais em preparação.'
                subtitle='Em breve, os catálogos do próximo ciclo estarão disponíveis aqui.'
              >
                <MagazineIcon color='$backgroundAltPrimary' size={80} />
              </EmptyState>
            ) : null}

            {!isLoadingNextCatalogs &&
            shouldRenderCatalogsCarousel(nextCatalogs, minLengthDesktop, minLengthMobile) ? (
              <Carousel
                onDotPress={(index) => {
                  handleTrackChange(index, 'proximo-ciclo', 'carrossel');
                }}
                onNextPress={() => {
                  handleTrackChange(null, 'proximo-ciclo', 'proximo');
                }}
                onPreviousPress={() => {
                  handleTrackChange(null, 'proximo-ciclo', 'anterior');
                }}
              >
                <PreviousButton />
                <CarouselContent
                  slidesToShow={isMobile ? slideToShow.mobile : slideToShow.desktop}
                  infinite={false}
                >
                  {Boolean(!isLoadingNextCatalogs && nextCatalogs?.length) &&
                    nextCatalogs.map((catalog: CardCatalogProps, index) => (
                      <Styles.CarouseItemlWrapper key={`next-catalogs-${catalog.brand}-${index}`}>
                        <CardCatalog {...catalog} isNextCycle index={index} />
                      </Styles.CarouseItemlWrapper>
                    ))}
                </CarouselContent>
                <NextButton />
              </Carousel>
            ) : (
              <Styles.Catalogs>
                {Boolean(!isLoadingNextCatalogs && nextCatalogs?.length) &&
                  nextCatalogs.map((catalog: CardCatalogProps, index) => (
                    <CardCatalog
                      key={`next-catalogs-${catalog.brand}-${index}`}
                      {...catalog}
                      isNextCycle
                      index={index}
                    />
                  ))}
              </Styles.Catalogs>
            )}
          </Styles.SectionWrapper>
        </Container>
      </Styles.Wrapper>
    </Flex>
  );
};
